import {
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  Inject,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
  signal,
} from '@angular/core';
import { type FormControl } from '@angular/forms';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import type { AutocompleteFactory } from '@frontendlib/common/types/autocomplete-factory';
import { CLOSE_POPOVER } from '@frontendlib/components/features/popover/types/close-popover.injection-token';
import { AccountTimeZoneSettingsService } from '@modules/account/services/account-timezone-settings.service';
import { CalendarsService } from '@modules/calendar-app/services/calendars.service';
import { DefaultReminderService } from '@modules/calendar-app/services/default-reminder.service';
import { EventInviteConfirmationService } from '@modules/calendar-app/services/event-invite-confirmation.service';
import { EventInviteService } from '@modules/calendar-app/services/event-invite.service';
import { EventsService } from '@modules/calendar-app/services/events.service';
import { Calendar } from '@modules/calendar-app/types/calendar';
import { CalendarEvent } from '@modules/calendar-app/types/calendar-event';
import { participantCapValidator } from '@modules/calendar-app/validators/participant-cap.validator';
import { getFirstError } from '@modules/common/utils/form';
import { htmlToPlainText } from '@modules/common/utils/html';
import { TimeZoneSeedingController } from '@modules/form-controls/services/timezone-seeding.controller';
import { toFakeLocal } from '@modules/form-controls/types/timezone-convert';
import { ModalService } from '@modules/modal/services/modal.service';
import { CalendarTenantScopeService } from '@modules/calendar-app/services/calendar-tenant-scope.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { type StitchRequestConfig } from '@modules/common/services/base-stitch.service';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { isValidTimeZone } from '@sharedlib/common/utils/timezone';
import { plainTextToHtml } from '@sharedlib/common/utils/plain-text-to-html';
import { BehaviorSubject, merge, of, Subject, throwError, type MonoTypeOperatorFunction } from 'rxjs';
import { catchError, filter, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'stch-event-popover-form',
  templateUrl: './event-popover-form.component.html',
  styleUrls: ['./event-popover-form.component.less'],
  standalone: false,
  providers: [EventInviteService, EventInviteConfirmationService],
})
export class EventPopoverFormComponent implements OnInit, OnChanges, OnDestroy {
  public calendarSuggestions: AutocompleteFactory<Calendar>;
  public form = new CalendarEvent().asFormGroup();
  public submitted = false;
  public saveInProgress = false;

  protected readonly tzSeeder = new TimeZoneSeedingController();
  /** The Reminder checkbox's words, with the account's default (Johanna, 2026-10-21). */
  protected readonly reminderLabel = computed(() => {
    const minutes = this.defaultReminderService.defaultMinutes();
    if (minutes == null) {
      return 'Reminder';
    }
    // A 0-minute default fires as the event begins (Johanna, 2026-10-22: not "0 minutes before").
    return minutes === 0
      ? 'Reminder set for the start of the event.'
      : `Reminder set for ${reminderLead(minutes)} before the event.`;
  });

  private stitchItemChanged = new BehaviorSubject<void>(null);
  private alive = new Subject<void>();
  /** Emitted right before the form is rebuilt so per-form subscriptions unbind from the discarded controls. */
  private readonly formRebuilt = new Subject<void>();
  /**
   * The user's own answer to "reminder?" on the current form, or `null` while they have not given one.
   * Only an explicit `false` blocks auto-seeding — an explicit `true` must still be re-seeded after an
   * all-day round trip, which clears the array on the way in. Deliberately not derived from the
   * control's `dirty` flag: `handleSubmit` calls `form.markAsPristine()`, which would drop the signal.
   */
  private reminderChoice: boolean | null = null;
  /** True while {@link setReminderChecked} writes, so the echo is not mistaken for a user choice. */
  private syncingReminderCheckbox = false;
  private readonly destroyRef = inject(DestroyRef);

  @Input() event = new CalendarEvent();

  @Output() close = new EventEmitter<void>();

  private readonly defaultReminderService = inject(DefaultReminderService);
  // #916: seeds a new item's zone from the person's own zone — the one the grid is drawn in.
  private readonly accountTimeZoneSettingsService = inject(AccountTimeZoneSettingsService);
  private readonly modalService = inject(ModalService);
  private readonly injector = inject(Injector);
  private readonly calendarTenantScope = inject(CalendarTenantScopeService, { optional: true });
  private readonly spaceContextStore = inject(SpaceContextStore, { optional: true });
  private draftTenantId: string | undefined;
  protected readonly newCalendarStatus = signal<'loading' | 'ready' | 'empty' | 'error'>('loading');

  protected get needsNewCalendar(): boolean {
    return !this.event.id && this.spaceContextStore?.type() === SpaceType.SHARED_SPACE;
  }

  private get ownerTenantId(): string | undefined {
    return this.event.accountId ?? this.draftTenantId;
  }

  private requestConfig(): StitchRequestConfig {
    const owner = this.ownerTenantId;
    return owner ? { requestContext: owningTenantContext(owner) } : {};
  }

  /** The grid can carry a calendar from the other rail section. Resolve it under the draft's owner. */
  private seedNewCalendar(): void {
    const tenantId = this.draftTenantId;
    if (!tenantId) {
      this.newCalendarStatus.set('error');
      this.form.controls.calendarId.setValue(null);
      return;
    }
    const form = this.form;
    const requestContext = owningTenantContext(tenantId);
    this.calendarsService
      .search({ limit: 100, archived: false, deleted: false }, { requestContext })
      .pipe(
        take(1),
        switchMap(({ items }) => {
          const owned = items.filter((calendar) => calendar.accountId === tenantId && !calendar.readOnly);
          const selected = owned.find((calendar) => calendar.id === form.controls.calendarId.value);
          const calendar = selected ?? owned.find((calendar) => calendar.default);
          return calendar
            ? of(calendar)
            : owned.length
              ? this.calendarsService.getDefaultCalendar(false, { requestContext }).pipe(
                  take(1),
                  map((dto) => new Calendar(dto)),
                )
              : of(undefined);
        }),
        this.untilFormReplaced(),
      )
      .subscribe({
        next: (calendar) => {
          const valid = calendar?.id && calendar.accountId === tenantId && !calendar.readOnly;
          this.newCalendarStatus.set(valid ? 'ready' : 'empty');
          form.controls.calendarId.setValue(valid ? calendar.id : null);
        },
        error: () => {
          this.newCalendarStatus.set('error');
          form.controls.calendarId.setValue(null);
        },
      });
  }

  constructor(
    private calendarsService: CalendarsService,
    private toasterService: ToasterService,
    private eventsService: EventsService,
    private inviteConfirmationService: EventInviteConfirmationService,
    @Optional() @Inject(CLOSE_POPOVER) private closePopoverToken,
  ) {}

  /**
   * Lifecycle
   */

  ngOnInit(): void {
    this.stitchItemChanged.pipe(takeUntil(this.alive)).subscribe(() => {
      this.formRebuilt.next();
      this.form = this.asFormGroup();
      this.saveInProgress = false;
      this.draftTenantId = this.needsNewCalendar
        ? (this.event.accountId ?? this.calendarTenantScope?.effectiveTenantId())
        : undefined;
      if (this.draftTenantId) this.event.accountId = this.draftTenantId;
      this.calendarSuggestions = this.calendarsService.getAutocompleteSuggestions({}, () =>
        owningTenantContext(this.ownerTenantId),
      );
      if (this.needsNewCalendar) this.newCalendarStatus.set('loading');
      // A new `event` is a fresh decision: the previous event's reminder choice must not suppress
      // seeding on the rebuilt form.
      this.reminderChoice = null;
      if (this.event.allDay) {
        this.form.controls.reminder.disable({ emitEvent: false });
      }
      // Re-bind seeding to every rebuilt form (a reused popover instance receives a new `event`);
      // the controller is restart-safe, so this tears down the prior binding rather than stacking.
      this.initTimeZoneSeeding();
      // Control-level subscriptions must follow the rebuilt form too, otherwise they keep driving the
      // discarded controls and the checkboxes stop reacting from the popover's second use onwards.
      this.trackAllDayChanges();
      this.trackReminderChanges();
      this.applyDefaultReminder();
      if (this.needsNewCalendar) this.seedNewCalendar();
    });
  }

  /** Unbinds per-form subscriptions on the next form rebuild, or on destroy — whichever comes first. */
  private untilFormReplaced<T>(): MonoTypeOperatorFunction<T> {
    return takeUntil<T>(merge(this.formRebuilt, this.alive));
  }

  /**
   * A new event inherits its timezone from the selected calendar, falling back to the
   * account/Settings timezone, then the browser, then UTC (mirrors the backend chain from
   * SBUX-767), never overriding a hand-picked value and never touching existing events. Resolution
   * and the override hint live in the shared TimeZoneSeedingController. Invoked from the
   * `stitchItemChanged` subscription so it re-binds to each rebuilt form.
   */
  private initTimeZoneSeeding(): void {
    const calendarTimeZone$ = this.form.controls.calendarId.valueChanges.pipe(
      startWith(this.form.controls.calendarId.value),
      filter(() => !this.needsNewCalendar || this.newCalendarStatus() !== 'loading'),
      // Empty/cleared calendars must emit null so the controller can fall back to settings → browser → UTC;
      // only fetch a calendar for a real id.
      switchMap((calendarId) =>
        calendarId
          ? this.calendarsService.getItem({ id: calendarId, accountId: this.ownerTenantId }).pipe(
              take(1),
              catchError(() => of(null)),
              map((calendar) => calendar?.timeZone ?? null),
            )
          : of(null),
      ),
    );

    this.tzSeeder.start({
      control: this.form.controls.timeZone,
      destroyRef: this.destroyRef,
      isExistingEntity: !!this.event.id,
      calendarTimeZone$,
      settingsTimeZone$: this.accountTimeZoneSettingsService.timeZoneOnce(),
      showCalendarOverrideHint: true,
      // Re-seed only on the initial resolve. A later calendar change keeps the shown numbers (and
      // just relabels the zone), matching the manual-tz-pick behaviour. (SBUX-829)
      onSeed: ({ timeZone }, isInitialSeed) => {
        if (isInitialSeed) {
          this.reseedNewEventWallClock(timeZone);
        }
      },
    });
  }

  /**
   * Re-express a pristine new event's date/time controls in the freshly resolved timezone. The form
   * is built synchronously (asFormGroup) before the async tz chain resolves, so the controls first
   * carry the browser wall clock; leaving them there makes the write path interpret those browser
   * numbers in the resolved zone and store the wrong instant — off by the browser↔resolved offset
   * (SBUX-829). The source instants are the event's own start/end (the same values asFormGroup
   * seeds from), so the shown moment is preserved and only its zone-relative wall clock changes.
   * Guarded to a pristine, non-all-day new event so it never disturbs an existing event's stored
   * time, a value the user already typed, or the "keep the numbers" manual-override behaviour.
   */
  private reseedNewEventWallClock(timeZone: string): void {
    if (this.event.id || this.form.controls.allDay.value || !isValidTimeZone(timeZone)) {
      return;
    }

    const reseedPair = (
      dateControl: FormControl<Date>,
      timeControl: FormControl<Date>,
      instant: Date | undefined,
    ): void => {
      if (!instant || dateControl.dirty || timeControl.dirty) {
        return;
      }
      const fakeLocal = toFakeLocal(instant, timeZone);
      dateControl.setValue(fakeLocal);
      timeControl.setValue(fakeLocal);
    };

    reseedPair(this.form.controls.startDate, this.form.controls.startTime, this.event.startTime);
    reseedPair(this.form.controls.endDate, this.form.controls.endTime, this.event.endTime);
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('event' in changes) {
      this.stitchItemChanged.next();
    }
  }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
    this.formRebuilt.complete();
  }

  /**
   * Actions
   */

  handleSubmit(): void {
    if (this.saveInProgress || (this.needsNewCalendar && this.newCalendarStatus() !== 'ready')) return;

    this.submitted = true;
    const isExistingEvent = !!this.form.controls.id.value;

    if (!this.form.valid) {
      this.toasterService.show({ text: getFirstError(this.form), icon: 'sapphire-close-small' });
      return;
    }

    if (this.form.pristine && isExistingEvent) {
      return;
    }

    const stitchItem = this.fromFormGroup();
    const inviteArgs = {
      form: this.form,
      isRemoteEvent: !!this.event?.isRemoteEvent,
      isExistingEvent,
    };

    const owner = this.ownerTenantId;
    const form = this.form;
    const event = this.event;
    const config = { emit: true, ...this.requestConfig() };
    if (owner) stitchItem.accountId = owner;
    let calendarFailed = false;
    let eventSaved = false;
    const calendarCheck$ = this.needsNewCalendar
      ? this.calendarsService.getItem({ id: stitchItem.calendarId, accountId: owner }).pipe(
          take(1),
          switchMap((calendar) =>
            calendar.accountId === owner &&
            !calendar.readOnly &&
            this.event === event &&
            this.form === form &&
            form.controls.calendarId.value === stitchItem.calendarId
              ? of(undefined)
              : throwError(() => new Error('Choose a calendar in this workspace.')),
          ),
          catchError((error: unknown) => {
            calendarFailed = true;
            return throwError(() => error);
          }),
        )
      : of(undefined);
    this.saveInProgress = this.needsNewCalendar;

    // Ask about invites BEFORE saving — mirrors full-form behavior.
    // If the user dismisses the alert without picking a button, askIfNeeded
    // never emits, so nothing below runs and the popover stays open for
    // further editing.
    calendarCheck$
      .pipe(
        switchMap(() => {
          this.saveInProgress = false;
          return this.inviteConfirmationService.askIfNeeded(inviteArgs);
        }),
        switchMap((choice) => {
          this.saveInProgress = true;
          if (this.needsNewCalendar && form.controls.calendarId.value !== stitchItem.calendarId) {
            calendarFailed = true;
            return throwError(() => new Error('Choose a calendar in this workspace.'));
          }

          const save$ = isExistingEvent
            ? this.eventsService.update(stitchItem, config)
            : this.eventsService.create(stitchItem, config);

          return save$.pipe(
            switchMap((savedEvent) => {
              eventSaved = true;
              this.form.markAsPristine();
              this.saveInProgress = false;

              if (choice === 'send') {
                return this.inviteConfirmationService.sendInvites(
                  savedEvent.id,
                  savedEvent.accountId ? { tenantItems: [savedEvent] } : undefined,
                );
              }
              return of(undefined);
            }),
          );
        }),
        this.untilFormReplaced(),
      )
      .subscribe({
        next: () => {
          this.handleClose();
        },
        error: () => {
          if (!eventSaved) {
            this.handleError(calendarFailed);
          } else {
            this.toasterService.show({
              text: 'Event saved but invitations failed to send.',
              icon: 'sapphire-close-small',
            });
            this.handleClose();
          }
        },
      });
  }

  handleMore() {
    const event = this.fromFormGroup();
    event.accountId = this.ownerTenantId;
    this.modalService.openFullForm(event, this.injector);
    this.handleClose();
  }

  handleError(calendarFailed = false) {
    this.saveInProgress = false;
    if (calendarFailed && this.needsNewCalendar) {
      this.newCalendarStatus.set('error');
      this.toasterService.show({
        text: 'Could not save this event. Choose a calendar in this workspace and reopen the form.',
        icon: 'sapphire-close-small',
      });
    } else {
      this.toasterService.show({ text: 'Could not save this event. Try again.', icon: 'sapphire-close-small' });
    }
  }

  handleClose() {
    this.close.emit();
    this.closePopoverToken?.next();
  }

  /**
   * Helpers
   */

  protected shouldRefreshList(prev, current) {
    return CalendarEvent.shouldRefreshList(prev, current);
  }

  protected fromFormGroup(): CalendarEvent {
    const event = CalendarEvent.fromFormGroup(this.form);
    event.description = this.descriptionForSave();
    return event;
  }

  /**
   * The quick form edits the description as plain text. Only a change the user made becomes HTML;
   * an untouched description keeps the stored HTML, formatting included.
   */
  private descriptionForSave(): string {
    const control = this.form.controls.description;
    return control.dirty ? plainTextToHtml(control.value ?? '') : (this.event?.description ?? '');
  }

  /**
   * Seeds the account default reminder onto a new event, re-bound to every rebuilt form.
   *
   * Stays subscribed on purpose (no `take(1)`, mirroring EventFormComponent): `listAll()` replays an
   * empty placeholder before the settings request resolves, so a one-shot subscription consumes that
   * `null` and never sees the real default — leaving a cold-cache popover with no reminder at all.
   * `shareReplay(1)` on `defaultMinutes$` replays the loaded value to each rebuild's subscription.
   */
  private applyDefaultReminder(): void {
    // Defensive only — see trackReminderChanges: this popover always gets a brand-new event, so `id`
    // is never set here. Kept so the seeding rule still reads correctly if that ever changes.
    if (this.event.id) return;

    this.defaultReminderService.defaultMinutes$
      .pipe(
        // Read all-day off the live control, not the input: the user can switch it on while the
        // settings request is still in flight, and a late default must respect that.
        filter(() => !this.form.controls.allDay.value && this.form.controls.reminders.length === 0),
        this.untilFormReplaced(),
      )
      .subscribe(() => {
        this.applyDefaultReminderIfNeeded();
      });
  }

  private trackAllDayChanges(): void {
    this.form.controls.allDay.valueChanges.pipe(this.untilFormReplaced()).subscribe((allDay) => {
      if (allDay) {
        this.setReminderChecked(false);
        this.form.controls.reminder.disable({ emitEvent: false });
        this.form.controls.reminders.clear();
      } else {
        this.form.controls.reminder.enable({ emitEvent: false });
        // Same defensive guard as applyDefaultReminder — unreachable today, see trackReminderChanges.
        if (this.event.id) return;

        this.applyDefaultReminderIfNeeded();
      }
    });
  }

  /**
   * The popover has no reminder editor — the single "Reminder" checkbox *is* the reminder, so it has to
   * drive the `reminders` array that actually gets saved. Without this, unchecking left the
   * auto-applied default reminder in the array and the created event still fired it, because
   * `reminder` is a UI-only flag that never reaches the payload.
   *
   * Programmatic writes go through {@link setReminderChecked}, which flags itself, so reaching the
   * body below always means the user themselves toggled the box.
   */
  private trackReminderChanges(): void {
    this.form.controls.reminder.valueChanges.pipe(this.untilFormReplaced()).subscribe((checked) => {
      if (this.syncingReminderCheckbox) {
        return;
      }

      this.reminderChoice = checked;

      if (!checked) {
        // Exact rather than surgical: this popover only ever opens on a new event, so the array holds
        // at most the default applied above. Every call site builds the event through
        // CalendarEvent.fromCalendarCell, and the drag paths land in fillFromStitch, which resets
        // `reminders` — there is never a full-form reminder here to lose.
        this.form.controls.reminders.clear();
        return;
      }

      // A no-op while the account default is still loading. Nothing is reverted and no warning is
      // needed: the recorded "yes" lets applyDefaultReminder seed the array the moment it resolves.
      this.defaultReminderService.applyIfNeeded(this.form.controls.reminders);
    });
  }

  /**
   * Applies the account default reminder and reflects it in the checkbox. Only an explicit "no" blocks
   * it: an all-day round trip clears the array on the way in, and a reminder the user deliberately
   * asked for has to come back on the way out, while one they deliberately removed must not.
   */
  private applyDefaultReminderIfNeeded(): void {
    if (this.reminderChoice === false) {
      return;
    }

    if (this.defaultReminderService.applyIfNeeded(this.form.controls.reminders)) {
      this.setReminderChecked(true);
    }
  }

  /**
   * Reflects the reminders array back into the checkbox.
   *
   * Must emit: the (deprecated) `<app-checkbox>` repaints from a `valueChanges` subscription rather
   * than a ControlValueAccessor `writeValue`, so a `{ emitEvent: false }` write would leave the
   * rendered box stale while the value and the array move underneath it. The flag — not event
   * suppression — is therefore what keeps `trackReminderChanges` from reading the echo as a choice.
   */
  private setReminderChecked(checked: boolean): void {
    this.syncingReminderCheckbox = true;
    try {
      this.form.controls.reminder.setValue(checked);
    } finally {
      // A latched flag would mute every later user click on the box, which is worse than whatever
      // threw — so clear it even then.
      this.syncingReminderCheckbox = false;
    }
  }

  protected asFormGroup() {
    const form = (this.event || new CalendarEvent()).asFormGroup();
    // Shown as plain text here; `descriptionForSave` turns an edit back into HTML.
    form.controls.description.setValue(htmlToPlainText(form.controls.description.value ?? ''), { emitEvent: false });
    form.controls.participants.addValidators(participantCapValidator(() => !!this.event?.isRemoteEvent));
    form.controls.participants.updateValueAndValidity({ emitEvent: false });
    return form;
  }
}

/** "15 minutes", "1 hour", "2 days": the lead time in its largest whole unit. */
function reminderLead(minutes: number): string {
  const [amount, unit] =
    minutes > 0 && minutes % 1440 === 0
      ? [minutes / 1440, 'day']
      : minutes > 0 && minutes % 60 === 0
        ? [minutes / 60, 'hour']
        : [minutes, 'minute'];
  return `${amount} ${unit}${amount === 1 ? '' : 's'}`;
}
